import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../../../constants/common'
import {
  getHasListingPublicationPublicUrl,
  getIsListingClosed,
  getIsListingPublicationInProgressError,
  getIsListingPublishBlocked,
  getListingPortalLabel,
  LISTING_PUBLICATION_IN_PROGRESS_ERROR_KEY,
} from '../../../../../../RentalPortalListing'
import {
  LISTING_PUBLICATION_ACTION,
  LISTING_PUBLICATION_ACTION_LABEL_KEY,
  LISTING_PUBLICATION_ACTION_NOTIFICATION_KEY,
  LISTING_PUBLICATION_BLOCKED_DRAFT_ACTIONS,
  LISTING_PUBLICATION_DISPATCH_ACTIONS,
  LISTING_PUBLICATION_PUBLISH_ACTIONS,
  LISTING_PUBLICATION_READINESS_ACTIONS,
  LISTING_PUBLICATION_RETRY_GATE_ACTION,
  LISTING_PUBLICATION_RETRY_NOTIFICATION_KEY,
  LISTING_PUBLICATION_SCHEDULED_DEACTIVATE_ACTIONS,
  LISTING_PUBLICATION_STATE_ACTIONS,
} from '../../RentalPortalListingProfilePublishings.constants'
import { getIsListingPublicationInProgress } from '../../RentalPortalListingProfilePublishings.utils'

function getIsListingPublicationRetryAction(action) {
  return action === LISTING_PUBLICATION_ACTION.retry
}

function getListingPublicationGateAction({ action, publication }) {
  return getIsListingPublicationRetryAction(action)
    ? (LISTING_PUBLICATION_RETRY_GATE_ACTION[publication.last_action] ?? action)
    : action
}

function getListingPublicationBlockReason({
  action,
  listing,
  portalConnections,
  publication,
  readinessBlockReason,
  t,
}) {
  const gateAction = getListingPublicationGateAction({ action, publication })

  if (!LISTING_PUBLICATION_DISPATCH_ACTIONS.includes(gateAction)) {
    return null
  }

  if (!listing?.permissions?.can_publish) {
    return t('RentalPortal.ListingPublicationNoPermissionTooltipKey')
  }

  if (
    portalConnections &&
    !portalConnections.portals.includes(publication.portal)
  ) {
    return t('RentalPortal.ListingPublicationNotConnectedTooltipKey', {
      portal: getListingPortalLabel(publication.portal),
    })
  }

  return LISTING_PUBLICATION_READINESS_ACTIONS.includes(gateAction)
    ? readinessBlockReason
    : null
}

function getIsListingPublicationOpenAdAction(action) {
  return action === LISTING_PUBLICATION_ACTION.openAd
}

function getIsListingPublicationActionRelevant({ action, publication }) {
  return LISTING_PUBLICATION_SCHEDULED_DEACTIVATE_ACTIONS.includes(action)
    ? Boolean(publication.scheduled_deactivate_at)
    : !getIsListingPublicationOpenAdAction(action) ||
        getHasListingPublicationPublicUrl(publication)
}

function toListingPublicationAction({
  action,
  listing,
  portalConnections,
  publication,
  readinessBlockReason,
  t,
}) {
  const blockReason = getListingPublicationBlockReason({
    action,
    listing,
    portalConnections,
    publication,
    readinessBlockReason,
    t,
  })

  return {
    disabled: Boolean(blockReason),
    label: t(LISTING_PUBLICATION_ACTION_LABEL_KEY[action]),
    tooltipContent: blockReason,
    value: action,
  }
}

function getIsListingPublicationActionBlocked({
  action,
  listing,
  publication,
}) {
  const gateAction = getListingPublicationGateAction({ action, publication })

  const isPublishOnClosedListing =
    getIsListingClosed(listing?.status) &&
    LISTING_PUBLICATION_PUBLISH_ACTIONS.includes(gateAction)

  const isPublishOnBlockedDraft =
    getIsListingPublishBlocked(listing) &&
    LISTING_PUBLICATION_BLOCKED_DRAFT_ACTIONS.includes(gateAction)

  return isPublishOnClosedListing || isPublishOnBlockedDraft
}

function getIsListingPublicationEditDeactivationAction(action) {
  return action === LISTING_PUBLICATION_ACTION.editDeactivation
}

export function getListingPortalConnections({ hasPortalsError, portals }) {
  return hasPortalsError || portals.length === 0
    ? null
    : {
        portals: portals
          .filter(connection => connection.connected)
          .map(connection => connection.portal),
      }
}

export function getListingPublicationScheduleInitialValue({
  action,
  publication,
}) {
  return getIsListingPublicationEditDeactivationAction(action)
    ? publication.scheduled_deactivate_at
    : publication.scheduled_publish_at
}

export function getListingPublicationScheduleBounds({ action, publication }) {
  return getIsListingPublicationEditDeactivationAction(action)
    ? { after: publication.scheduled_publish_at }
    : { before: publication.scheduled_deactivate_at }
}

export function getListingPublicationActions({
  listing,
  portalConnections,
  publication,
  readinessBlockReason,
  t,
}) {
  const isInProgress = getIsListingPublicationInProgress(publication)

  const { options = EMPTY_LIST, primary = null } =
    LISTING_PUBLICATION_STATE_ACTIONS[publication?.sync_status] ?? EMPTY_OBJECT

  function toAction(action) {
    return toListingPublicationAction({
      action,
      listing,
      portalConnections,
      publication,
      readinessBlockReason,
      t,
    })
  }

  function getIsActionAvailable(action) {
    return (
      !getIsListingPublicationActionBlocked({ action, listing, publication }) &&
      getIsListingPublicationActionRelevant({ action, publication })
    )
  }

  return {
    isMenuDisabled: isInProgress,
    menuTooltipContent: isInProgress
      ? t('RentalPortal.ListingPublicationInProgressTooltipKey')
      : null,
    options: options.filter(getIsActionAvailable).map(toAction),
    primary:
      primary && getIsActionAvailable(primary)
        ? toAction(primary)
        : EMPTY_OBJECT,
  }
}

export function getListingPublicationActionNotificationKey({
  action,
  publication,
}) {
  return getIsListingPublicationRetryAction(action)
    ? LISTING_PUBLICATION_RETRY_NOTIFICATION_KEY[publication.last_action]
    : LISTING_PUBLICATION_ACTION_NOTIFICATION_KEY[action]
}

export function getListingPublicationActionErrorNotification({
  error,
  t,
  onRetry,
}) {
  return getIsListingPublicationInProgressError(error)
    ? { title: t(LISTING_PUBLICATION_IN_PROGRESS_ERROR_KEY) }
    : {
        textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
        title: t('RentalPortal.ListingPublicationActionErrorKey'),
        onClickTextButton: onRetry,
      }
}
